import { useEffect } from 'react';
import Button from 'components/controls/buttons/button';
import ChevronLeftIcon from 'assets/icons/chevron-l.svg';
import ChevronRightIcon from 'assets/icons/chevron-r.svg';
import ZoomInIcon from 'assets/icons/zoom-in.svg';
import ZoomOutIcon from 'assets/icons/zoom-out.svg';
import MetLogoIcon from 'assets/icons/met-logo.svg';
import { Button as IconButton, MenuButton } from '../buttons';
import styles from './bottom-panel.module.scss';
import type { ViewerAction } from '../../types';

export default function BottomPanel(props: BottomPanelProps) {
	// We render both of these and rely on CSS to display the correct one to prevent layout shifting on load.
	return (
		<>
			<BottomPanelMobile {...props} />
			<BottomPanelDesktop {...props} />
		</>
	);
}

function BottomPanelMobile({
	title,
	isFirst,
	isLast,
	dispatch,
	showTranscription,
	showTranslation,
}: BottomPanelProps) {
	return (
		<div className={[styles.container, styles.mobileContainer].join(' ')}>
			<div className={styles.leftGroup}>
				<p title={title} className={styles.title}>
					{title}
				</p>
			</div>
			<div className={styles.centerGroup}>
				<div className={styles.buttonGroup}>
					<IconButton
						label="Previous"
						icon={<ChevronLeftIcon />}
						onClick={() => dispatch({ type: 'go_to_previous_page' })}
						disabled={isFirst}
						className={styles.button}
					/>
					<IconButton
						label="Next"
						icon={<ChevronRightIcon />}
						onClick={() => dispatch({ type: 'go_to_next_page' })}
						disabled={isLast}
						className={styles.button}
					/>
				</div>
			</div>
			<div className={styles.rightGroup}>
				<IconButton
					jsxLabel={<span>Reading tools</span>}
					icon={null}
					showLabel={true}
					aria-haspopup={true}
					aria-expanded={showTranscription || showTranslation}
					onClick={() =>
						dispatch({
							type: showTranscription
								? 'hide_transcription'
								: 'show_transcription',
						})
					}
					className={styles.mobileReadingToolsTrigger}
				/>
			</div>
		</div>
	);
}

function BottomPanelDesktop({
	title,
	isFirst,
	isLast,
	hasError,
	canZoomIn,
	canZoomOut,
	zoomIn,
	zoomOut,
	dispatch,
	showTranscription,
	showTranslation,
}: BottomPanelProps) {
	// Close the reading tools menu when all options are enabled.
	useEffect(() => {
		if (showTranscription && showTranslation) {
			document.body.click();
		}
	}, [showTranscription, showTranslation]);

	const handleClick = (optionValue: string) => {
		if (optionValue === 'showTranscription') {
			dispatch({
				type: showTranscription ? 'hide_transcription' : 'show_transcription',
			});
		}
		if (optionValue === 'showTranslation') {
			dispatch({
				type: showTranslation ? 'hide_translation' : 'show_translation',
			});
		}
	};

	const readingToolsOptionDescriptors: SelectMenuOptionDescriptor[] = [
		{
			label: 'Show transcription',
			value: 'showTranscription',
			disabled: showTranscription,
		},
		{
			label: 'Show translation',
			value: 'showTranslation',
			disabled: showTranslation,
		},
	];

	return (
		<div className={[styles.container, styles.desktopContainer].join(' ')}>
			<div className={styles.leftGroup}>
				<MetLogoIcon className={styles.metLogo} />
				<p title={title} className={styles.title}>
					{title}
				</p>
			</div>
			<div className={styles.centerGroup}>
				<div className={styles.buttonGroup}>
					<IconButton
						label="Previous"
						icon={<ChevronLeftIcon />}
						onClick={() => dispatch({ type: 'go_to_previous_page' })}
						disabled={isFirst}
						className={styles.button}
					/>
					<IconButton
						label="Next"
						icon={<ChevronRightIcon />}
						onClick={() => dispatch({ type: 'go_to_next_page' })}
						disabled={isLast}
						className={styles.button}
					/>
				</div>
			</div>
			<div className={styles.rightGroup}>
				<div className={styles.buttonGroup}>
					<IconButton
						icon={<ZoomInIcon />}
						label="Zoom in"
						onClick={zoomIn}
						className={styles.button}
						disabled={hasError || !canZoomIn}
					/>
					<IconButton
						icon={<ZoomOutIcon />}
						label="Zoom out"
						onClick={zoomOut}
						className={styles.button}
						disabled={hasError || !canZoomOut}
					/>
				</div>
				<MenuButton
					label="Reading tools"
					icon={null}
					menuContents={
						<ul
							className={styles.readingToolsOptions}
							role="listbox"
							aria-multiselectable="true"
						>
							{readingToolsOptionDescriptors.map((option) => {
								const handleReadingToolClick = () => handleClick(option.value);

								return (
									<li key={option.value} className={styles.readingToolsOption}>
										<Button
											appearance={null}
											disabled={option.disabled}
											aria-disabled={option.disabled}
											onClick={handleReadingToolClick}
											value={option.value}
											className={styles.readingToolsOptionTrigger}
										>
											{option.label}
										</Button>
									</li>
								);
							})}
						</ul>
					}
				/>
			</div>
		</div>
	);
}

interface BottomPanelProps {
	title: string;
	isFirst: boolean;
	isLast: boolean;
	hasError: boolean;
	canZoomIn: boolean;
	canZoomOut: boolean;
	showTranscription: boolean;
	showTranslation: boolean;
	zoomIn?: () => void;
	zoomOut?: () => void;
	dispatch: React.Dispatch<ViewerAction>;
}

type SelectMenuOptionDescriptor = {
	label: string;
	value: string;
	disabled: boolean;
};
